import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { Box, Button, Card, Chip, CircularProgress, Stack, Typography } from '@mui/material';

import {
  getGetProcessTalentsQueryKey,
  ProcessResponseDTO,
  TalentProcessResponseDTO,
  TalentProcessResponseDTOStatus
} from '@/api/generated';
import HiddenItemCounter from '@/components/HiddenItemCounter/HiddenItemCounter';
import TalentContactInfo from '@/components/TalentContactInfo/TalentContactInfo';
import TalentProcessActionsMenu from '@/components/TalentProcessActionsMenu';
import { UserAvatar } from '@/components/UserAvatar';
import { useDirection } from '@/hooks/useDirection';
import { toTalent } from '@/root/linker';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

import TestSessionSummary from './TestSessionSummary';

const getProgressByStatus = (status?: TalentProcessResponseDTOStatus): number => {
  switch (status) {
    case TalentProcessResponseDTOStatus.REGISTERED:
      return 25;
    case TalentProcessResponseDTOStatus.IN_TEST:
      return 50;
    case TalentProcessResponseDTOStatus.COMPLETED:
    case TalentProcessResponseDTOStatus.REFUSED:
      return 100;
    default:
      return 0;
  }
};

interface TalentCardProps {
  talent: TalentProcessResponseDTO;
  process?: ProcessResponseDTO;
  showDropdown?: boolean;
  showAddButton?: boolean;
  showRemoveButton?: boolean;
  showTestSessionInfo?: boolean;
  showProgress?: boolean;
  showTalentContactInfo?: boolean;
  onAdd?: (talentId: string) => void;
  onRemove?: (talentId: string) => void;
  // isChecked?: boolean;
  // onToggle?: () => void;
}

const TalentCard: FC<TalentCardProps> = ({
  talent,
  process,
  showDropdown,
  showAddButton,
  showRemoveButton,
  showTestSessionInfo = true,
  showProgress = true,
  showTalentContactInfo = false,
  onAdd,
  onRemove
}) => {
  const { t } = useTranslation(['processes', 'common']);
  const { firstName, lastName, email, phone, idNumber } = talent;
  const name = t('common:header.user.fullName', { firstName, lastName });
  const progress = useMemo(() => getProgressByStatus(talent.status), [talent.status]);

  const queryClient = useQueryClient();
  const { end } = useDirection();

  const handleRemoveSuccess = useCallback(() => {
    if (process?.id) {
      queryClient.invalidateQueries(getGetProcessTalentsQueryKey(process.id));
    }
  }, [process?.id, queryClient]);

  const handleAddTalent = useCallback(() => onAdd?.(talent.id!), [onAdd, talent.id]);
  const handleRemoveTalent = useCallback(() => onRemove?.(talent.id!), [onRemove, talent.id]);

  return (
    <>
      <Card
        sx={{ bgcolor: 'background.default', border: borders.default, borderColor: 'grey.400', overflow: 'visible' }}
        elevation={0}
      >
        <Stack direction="row" p={2} justifyContent="space-between">
          <Stack direction="row" gap={2} flexWrap="wrap">
            <Stack
              component={Link}
              direction="row"
              gap={1}
              alignItems="center"
              to={toTalent({
                talentId: talent.id!
              })}
              sx={{
                textDecoration: 'none'
              }}
            >
              {/* <Box sx={{ width: 32, height: 32 }} onClick={onToggle}>
            {isChecked ? <CheckboxChecked /> : <CheckboxEmpty />}
          </Box> */}
              <Box sx={{ position: 'relative' }}>
                <UserAvatar user={talent} variant="talent-xsmall" sx={{ m: showProgress ? '1px' : 0 }} />
                {showProgress && (
                  <>
                    <CircularProgress
                      sx={{ position: 'absolute', pointerEvents: 'none', top: 0, left: 0, color: '#c7ceee' }}
                      variant="determinate"
                      value={100}
                      size={34}
                      thickness={2}
                    />
                    <CircularProgress
                      sx={{ position: 'absolute', pointerEvents: 'none', top: 0, left: 0, color: '#435ac0' }}
                      variant="determinate"
                      value={progress}
                      size={34}
                      thickness={2}
                    />
                  </>
                )}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: typography.fontWeightMedium, color: 'text.primary' }}>
                {name}
              </Typography>
              <Chip variant="tag" label={t('layouts.processes.details.info.talent.applicant')} />
              {showTestSessionInfo && (
                <>
                  <TestSessionSummary summary={talent.latestTestSession} />
                  {!!talent.testSessionsCount && talent.testSessionsCount > 1 && (
                    <HiddenItemCounter hiddenItemsCount={talent.testSessionsCount - 1} isDisabled />
                  )}
                </>
              )}
            </Stack>
            {showTalentContactInfo && <TalentContactInfo email={email} phone={phone} idNumber={idNumber} />}
          </Stack>
          {showDropdown && process && (
            <TalentProcessActionsMenu
              process={process}
              talent={talent}
              onRemoveSuccess={handleRemoveSuccess}
              menuProps={{
                transformOrigin: {
                  vertical: 'top',
                  horizontal: end
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: end
                }
              }}
              autoClose
            />
          )}
          {showAddButton && (
            <Button sx={{ py: 0.5 }} onClick={handleAddTalent}>
              {t('layouts.process.menu.addToProcess.add')}
            </Button>
          )}
          {showRemoveButton && (
            <Button sx={{ py: 0.5 }} onClick={handleRemoveTalent}>
              {t('layouts.process.menu.addToProcess.remove')}
            </Button>
          )}
        </Stack>
      </Card>
    </>
  );
};

export default TalentCard;
